// packages/ops-platform-v3/src/frontend/expoams/ExpoAmsRouter.tsx
//
// Ported from StationJo's src/components/expoams/ExpoAmsRouter.tsx (Plan 09 Task 8).
// - UserContext (isAdmin / canAccessExpoAms) swapped for Plan 07's useAuth hook.
// - Access is now gated on opsv3.expoams.* atomic privileges (wildcard '*' bypasses).
// - Plan 10: sheet config is fetched at runtime via expoamsClient.getSheetConfig()
//   (dropped VITE_EXPOAMS_SHEET_IDS build-time bake).

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/AuthContext.js';
import ScanView from './ScanView.js';
import InventoryView from './InventoryView.js';
import TransactionHistory from './TransactionHistory.js';
import SheetSettings from './SheetSettings.js';
import type { ExpoAmsView } from './types.js';
import { expoamsClient } from './services/index.js';
import './ExpoAmsRouter.css';

export const ExpoAmsRouter: React.FC = () => {
  const { payload } = useAuth();
  const privileges = payload?.privileges ?? [];
  const hasWildcard = privileges.includes('*');
  const canManageSheets = hasWildcard || privileges.includes('opsv3.expoams.manage_sheets');
  const canAccessExpoAms =
    hasWildcard ||
    canManageSheets ||
    privileges.includes('opsv3.expoams.inventory.read') ||
    privileges.includes('opsv3.expoams.scan');

  // Navigation
  const [currentView, setCurrentView] = useState<ExpoAmsView>('scan');

  // Sheet state
  const [sheetIds, setSheetIds] = useState<string[]>([]);
  const [sheetNames, setSheetNames] = useState<Record<string, string>>({});
  const [selectedSheetId, setSelectedSheetId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Incremented after every successful scan; Inventory/History drop their
  // per-sheet caches when it changes.
  const [dataVersion, setDataVersion] = useState(0);
  const handleTransactionComplete = useCallback(() => setDataVersion((v) => v + 1), []);

  // Load sheet config at runtime from Firestore via getSheetConfig(). Plan 10
  // removed the build-time VITE_EXPOAMS_SHEET_IDS bake; sheet IDs now live only
  // in Firestore and are fetched on mount (and on demand via onSheetsChanged).
  const loadSheets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const config = await expoamsClient.getSheetConfig();
      const ids = config.sheetIds;
      setSheetIds(ids);
      if (ids.length > 0) {
        // Titles come with the config (persisted in Firestore) so the picker
        // needs no Sheets API calls. Only ids without a stored title — sheets
        // registered before titles were persisted, or a config served by an
        // older functions build — fall back to the per-sheet lookup.
        const names: Record<string, string> = { ...(config.sheetNames ?? {}) };
        const missing = ids.filter((id) => names[id] === undefined);
        if (missing.length > 0) {
          const { sheetNames: fetched } = await expoamsClient.getSheetNames({
            sheetIds: missing,
          });
          Object.assign(names, fetched);
        }
        setSheetNames(names);
        // No auto-default: user must explicitly pick a sheet each session.
        // Only clear an existing selection if the admin removed that sheet.
        // (This mirrors the StationJo fix; field reports indicated team
        // members were chaining transactions against an unintentional
        // default-selected sheet.)
        if (selectedSheetId && !ids.includes(selectedSheetId)) {
          setSelectedSheetId('');
        }
      } else {
        setSheetNames({});
        setSelectedSheetId('');
      }
    } catch (err) {
      // CookieExpiredError (from id-token-manager) and UnauthenticatedError
      // surface here as generic load failures; the app-level ProtectedRoute
      // re-checks auth on next mount. Refinement is future work.
      console.error('Failed to load sheet config:', err);
      setError('Failed to load inventory sheets');
    } finally {
      setLoading(false);
    }
  }, [selectedSheetId]);

  // Bootstrap-only: `loadSheets` is a useCallback([selectedSheetId]), so adding
  // it as a dep would re-fire on every selection change. The selection-change
  // reload is handled elsewhere; this effect only needs to kick the initial
  // load at mount time.
  useEffect(() => {
    void loadSheets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!canAccessExpoAms) {
    return (
      <div className="expoams-error">Access denied. Inventory read or scan privilege required.</div>
    );
  }

  const renderContent = () => {
    switch (currentView) {
      case 'scan':
        return (
          <ScanView
            sheetIds={sheetIds}
            sheetNames={sheetNames}
            selectedSheetId={selectedSheetId}
            onSelectSheet={setSelectedSheetId}
            userName={payload?.user.email ?? 'Unknown'}
            onTransactionComplete={handleTransactionComplete}
          />
        );
      case 'inventory':
        return (
          <InventoryView
            sheetIds={sheetIds}
            sheetNames={sheetNames}
            selectedSheetId={selectedSheetId}
            onSelectSheet={setSelectedSheetId}
            dataVersion={dataVersion}
          />
        );
      case 'history':
        return (
          <TransactionHistory
            sheetIds={sheetIds}
            sheetNames={sheetNames}
            selectedSheetId={selectedSheetId}
            onSelectSheet={setSelectedSheetId}
            dataVersion={dataVersion}
          />
        );
      case 'settings':
        return canManageSheets ? (
          <SheetSettings sheetIds={sheetIds} sheetNames={sheetNames} onSheetsChanged={loadSheets} />
        ) : (
          <div className="expoams-error">Admin access required for settings.</div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="expoams-router">
      {/* Top Tab Bar */}
      <div className="expoams-tab-bar">
        <button
          className={`expoams-tab ${currentView === 'scan' ? 'active' : ''}`}
          onClick={() => setCurrentView('scan')}
        >
          <span className="expoams-tab-icon">📷</span>
          Scan
        </button>
        <button
          className={`expoams-tab ${currentView === 'inventory' ? 'active' : ''}`}
          onClick={() => setCurrentView('inventory')}
        >
          <span className="expoams-tab-icon">📦</span>
          Inventory
        </button>
        <button
          className={`expoams-tab ${currentView === 'history' ? 'active' : ''}`}
          onClick={() => setCurrentView('history')}
        >
          <span className="expoams-tab-icon">📋</span>
          History
        </button>
        {canManageSheets && (
          <button
            className={`expoams-tab ${currentView === 'settings' ? 'active' : ''}`}
            onClick={() => setCurrentView('settings')}
          >
            <span className="expoams-tab-icon">⚙</span>
            Settings
          </button>
        )}
      </div>

      {error && <div className="expoams-error">{error}</div>}
      {loading ? (
        <div className="expoams-loading">Loading inventory sheets...</div>
      ) : (
        <div className="expoams-content">{renderContent()}</div>
      )}
    </div>
  );
};
